import React, { useEffect, useState } from "react";
import { api, fmtMoney, formatApiErrorDetail } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PencilSimple, Plus, Trash, FloppyDisk } from "@phosphor-icons/react";
import { toast } from "sonner";
import MaterialAutocomplete from "@/components/MaterialAutocomplete";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

/**
 * PurchaseEditSheet — full editor for an existing Purchase (bill).
 * Lets the user edit header AND every line item (description, HSN, qty, rate,
 * GST%). GST split is live-recomputed and re-persisted on save.
 */
export default function PurchaseEditSheet({ purchase, projects, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !purchase) return;
    setForm({
      vendor: purchase.vendor || "",
      vendor_gstin: purchase.vendor_gstin || "",
      invoice_number: purchase.invoice_number || "",
      project_id: purchase.project_id || "",
      date: purchase.date || "",
      notes: purchase.notes || "",
      is_interstate: !!purchase.is_interstate,
      freight_amount: Number(purchase.freight_amount || 0),
      insurance_amount: Number(purchase.insurance_amount || 0),
      packing_amount: Number(purchase.packing_amount || 0),
      other_charges_amount: Number(purchase.other_charges_amount || 0),
      items: (purchase.items && purchase.items.length)
        ? purchase.items.map((it) => ({
            description: it.description || "",
            hsn: it.hsn || "",
            sku: it.sku || "",
            unit: it.unit || "nos",
            qty: Number(it.qty || 1),
            unit_price: Number(it.unit_price || 0),
            gst_rate: Number(it.gst_rate || 0),
          }))
        // Legacy flat rows — synthesize a single item
        : [{
            description: purchase.item || "",
            hsn: "",
            sku: "",
            unit: "nos",
            qty: Number(purchase.quantity || 1),
            unit_price: Number(purchase.unit_price || 0),
            gst_rate: 0,
          }],
    });
  }, [open, purchase]);

  const updateLine = (i, key, value) =>
    setForm((f) => ({ ...f, items: f.items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)) }));
  const addLine = () =>
    setForm((f) => ({ ...f, items: [...f.items, { description: "", hsn: "", sku: "", unit: "nos", qty: 1, unit_price: 0, gst_rate: 18 }] }));
  const removeLine = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  // Live GST recompute for the totals preview
  const items = form?.items || [];
  const subtotal = items.reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0), 0);
  const totalTax = items.reduce(
    (s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * Number(it.gst_rate || 0) / 100,
    0,
  );
  const cgst = form?.is_interstate ? 0 : totalTax / 2;
  const sgst = form?.is_interstate ? 0 : totalTax / 2;
  const igst = form?.is_interstate ? totalTax : 0;
  const grand = subtotal + totalTax;

  const save = async () => {
    if (!form.vendor.trim()) return toast.error("Vendor is required");
    const cleanItems = form.items
      .map((it) => ({
        description: (it.description || "").trim(),
        hsn: it.hsn || "",
        sku: it.sku || "",
        unit: it.unit || "nos",
        qty: Math.max(Number(it.qty) || 0, 0) || 1,
        unit_price: Math.max(Number(it.unit_price) || 0, 0),
        gst_rate: Math.max(Number(it.gst_rate) || 0, 0),
      }))
      .filter((it) => it.description || it.unit_price > 0);
    if (!cleanItems.length) return toast.error("At least one line item is required");

    setSaving(true);
    try {
      await api.put(`/purchases/${purchase.id}`, {
        vendor: form.vendor,
        vendor_gstin: form.vendor_gstin || "",
        invoice_number: form.invoice_number || "",
        project_id: form.project_id || null,
        date: form.date,
        notes: form.notes,
        is_interstate: form.is_interstate,
        items: cleanItems,
        freight_amount: Number(form.freight_amount || 0),
        insurance_amount: Number(form.insurance_amount || 0),
        packing_amount: Number(form.packing_amount || 0),
        other_charges_amount: Number(form.other_charges_amount || 0),
      });
      toast.success("Purchase updated");
      onSaved?.();
      onOpenChange?.(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[760px] sm:max-w-2xl lg:w-[min(960px,95vw)] lg:max-w-none overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-display flex items-center gap-2">
            <PencilSimple size={18} className="text-[#EA580C]" /> Edit Purchase Bill {purchase.invoice_number ? `· ${purchase.invoice_number}` : ""}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-5 space-y-5" data-testid="purchase-edit-form">
          {/* Shared unit datalist — used by every line item */}
          <datalist id="purchase-unit-options">
            <option value="nos" /><option value="pcs" /><option value="pack" /><option value="carton" /><option value="sheet" /><option value="coil" /><option value="set" /><option value="pair" /><option value="roll" /><option value="bundle" /><option value="drum" /><option value="bag" /><option value="box" /><option value="lot" />
            <option value="kg" /><option value="g" /><option value="ton" />
            <option value="meter" /><option value="mm" /><option value="cm" /><option value="ft" /><option value="inch" />
            <option value="sqm" /><option value="sqft" /><option value="cum" /><option value="cuft" />
            <option value="litre" /><option value="ml" />
            <option value="hour" /><option value="day" /><option value="week" /><option value="month" />
          </datalist>
          {/* Header */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Vendor Name">
              <Input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} data-testid="edit-purchase-vendor" />
            </Field>
            <Field label="Vendor GSTIN">
              <Input value={form.vendor_gstin} onChange={(e) => setForm({ ...form, vendor_gstin: e.target.value })} className="font-mono uppercase" />
            </Field>
            <Field label="Invoice / Bill Number">
              <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="edit-purchase-invoice-number" />
            </Field>
            <Field label="Bill Date">
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="edit-purchase-date" />
            </Field>
            <Field label="Project (optional)">
              <Select
                value={form.project_id || "__none__"}
                onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}
              >
                <SelectTrigger data-testid="edit-purchase-project"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Tax Type">
              <Select value={form.is_interstate ? "igst" : "cgst"} onValueChange={(v) => setForm({ ...form, is_interstate: v === "igst" })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="cgst">Intra-state (CGST + SGST)</SelectItem>
                  <SelectItem value="igst">Inter-state (IGST)</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Line items table — every field editable */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="px-3 py-2 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">
                Line Items · {items.length}
              </div>
              <button onClick={addLine} data-testid="edit-purchase-add-line" className="text-[11px] font-semibold text-[#EA580C] hover:underline flex items-center gap-1">
                <Plus size={12} /> Add row
              </button>
            </div>
            <Table className="bc-list text-[12.5px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Description</TableHead>
                  <TableHead className="w-24">HSN</TableHead>
                  <TableHead className="w-20 text-right">Qty</TableHead>
                  <TableHead className="w-20">Unit</TableHead>
                  <TableHead className="w-28 text-right">Rate</TableHead>
                  <TableHead className="w-20 text-right">GST%</TableHead>
                  <TableHead className="w-28 text-right">Line ₹</TableHead>
                  <TableHead className="w-8" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it, i) => {
                  const line = Number(it.qty || 0) * Number(it.unit_price || 0);
                  return (
                    <TableRow key={`purchase-edit-line-${i}`} data-testid={`purchase-edit-line-${i}`}>
                      <TableCell>
                        <MaterialAutocomplete
                          value={it.description}
                          onChange={(v) => updateLine(i, "description", v)}
                          onPick={(m) => {
                            setForm((f) => ({
                              ...f,
                              items: f.items.map((row, idx) =>
                                idx === i
                                  ? {
                                      ...row,
                                      description: m.description,
                                      hsn: m.hsn || row.hsn,
                                      gst_rate: m.gst_rate || row.gst_rate || 18,
                                      unit_price: m.last_unit_price || row.unit_price,
                                    }
                                  : row,
                              ),
                            }));
                          }}
                          testid={`edit-purchase-line-desc-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input value={it.hsn} onChange={(e) => updateLine(i, "hsn", e.target.value)} className="h-7 font-mono text-[11.5px]" />
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={it.qty} onChange={(e) => updateLine(i, "qty", e.target.value)} className="h-7 text-right font-mono-num" />
                      </TableCell>
                      <TableCell>
                        <Input
                          list="purchase-unit-options"
                          value={it.unit || "nos"}
                          onChange={(e) => updateLine(i, "unit", e.target.value)}
                          className="h-7 text-[12px] uppercase tracking-wider"
                          placeholder="nos"
                          data-testid={`edit-purchase-line-unit-${i}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={it.unit_price} onChange={(e) => updateLine(i, "unit_price", e.target.value)} className="h-7 text-right font-mono-num" />
                      </TableCell>
                      <TableCell>
                        <Input type="number" value={it.gst_rate} onChange={(e) => updateLine(i, "gst_rate", e.target.value)} className="h-7 text-right font-mono-num" />
                      </TableCell>
                      <TableCell className="text-right font-mono-num font-semibold">{fmtMoney(line)}</TableCell>
                      <TableCell>
                        <button onClick={() => removeLine(i)} className="text-slate-400 hover:text-red-600"><Trash size={12} /></button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Totals preview */}
          <div className="flex justify-end">
            <div className="w-72 text-[13px] space-y-1 font-mono-num">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span>{fmtMoney(subtotal)}</span></div>
              {form.is_interstate ? (
                <div className="flex justify-between text-slate-600"><span>IGST</span><span>{fmtMoney(igst)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between text-slate-600"><span>CGST</span><span>{fmtMoney(cgst)}</span></div>
                  <div className="flex justify-between text-slate-600"><span>SGST</span><span>{fmtMoney(sgst)}</span></div>
                </>
              )}
              <div className="flex justify-between font-semibold text-[15px] text-[#EA580C] pt-1 border-t border-slate-200">
                <span>Grand Total</span>
                <span data-testid="edit-purchase-total">{fmtMoney(grand)}</span>
              </div>
            </div>
          </div>

          {/* Additional charges — auto-syncs to Expenses on save */}
          <div className="border border-slate-200 rounded-sm bg-slate-50">
            <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">Additional charges (freight etc.)</div>
              <div className="text-[11px] text-slate-500">Auto-synced to Expenses with project link</div>
            </div>
            <div className="p-3 grid grid-cols-4 gap-3">
              <Field label="Freight (₹)"><Input type="number" value={form.freight_amount || 0} onChange={(e) => setForm({ ...form, freight_amount: e.target.value })} className="font-mono-num text-right" data-testid="edit-freight" /></Field>
              <Field label="Insurance (₹)"><Input type="number" value={form.insurance_amount || 0} onChange={(e) => setForm({ ...form, insurance_amount: e.target.value })} className="font-mono-num text-right" /></Field>
              <Field label="Packing (₹)"><Input type="number" value={form.packing_amount || 0} onChange={(e) => setForm({ ...form, packing_amount: e.target.value })} className="font-mono-num text-right" /></Field>
              <Field label="Other (₹)"><Input type="number" value={form.other_charges_amount || 0} onChange={(e) => setForm({ ...form, other_charges_amount: e.target.value })} className="font-mono-num text-right" /></Field>
            </div>
          </div>

          <Field label="Notes">
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Field>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-sm">Cancel</Button>
            <Button
              onClick={save}
              disabled={saving || !form.vendor.trim() || items.length === 0}
              data-testid="edit-purchase-save"
              className="flex-1 rounded-sm bg-[#EA580C] hover:bg-[#c2410c] text-white"
            >
              <FloppyDisk size={14} className="mr-2" />
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
